import type {
  AggregatePoint,
  Filters,
  IncidentItem,
  Meta,
  RoadUser,
  SeriesPoint,
  Stats,
} from '../../src/api/queries';
import type {
  TrafficCalibration,
  TrafficFacets,
  TrafficStats,
} from '../../src/api/traffic-queries';
import { DEFAULT_SOURCE } from '../../src/source';
import { canonicalList } from '../../src/wire';

/**
 * The typed client for the API in spec §6: URL builders, one `fetch` wrapper, a typed
 * {@link ApiError} and its Estonian sentence. Response types arrive as `import type`, so no server
 * code reaches the bundle; query strings are hand-built because `URLSearchParams` spells a space
 * `+`, and an empty value is dropped so a cleared filter and an untouched one share one cache key.
 */

/** A query parameter before encoding. `null`/`undefined`/`''` mean "not set" and are dropped. */
type Param = readonly [string, string | number | null | undefined];

/** The one list-valued filter, in the canonical form the URL carries, so the request and the
 * address bar cannot spell one selection two ways. Empty is `''`, which {@link query} drops. */
function municipalityParam(municipalities: readonly string[] | null | undefined): string {
  return canonicalList(municipalities ?? []).join(',');
}

function query(params: readonly Param[]): string {
  const parts = params
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

/**
 * Whether the endpoint being addressed buckets its answer by `granularity`.
 *
 * Only `/api/series` and `/api/aggregates` do. `/api/stats` returns one object and `/api/incidents`
 * returns rows, and `queries.ts` never reads the field on either path — it is part of `Filters`
 * purely because the four endpoints share one type.
 */
type Bucketing = 'bucketed' | 'unbucketed';

/**
 * The filter block every data endpoint shares, in the order spec §6 documents it.
 *
 * `prefecture` passes through untouched, sentinel included: `UNSPECIFIED` selects the rows with no
 * prefecture at all — the UI's "Määramata" bucket — and is a filter, not the absence of one.
 *
 * **`granularity` is sent only where it changes the answer**, and that is a cache decision, not a
 * tidiness one. These URLs are the React Query keys (every call site keys on the string this module
 * builds), so a parameter in the URL that the server ignores still splits the cache: clicking
 * "nädal" would evict the `/api/stats` entry the page just showed and — worse — reset
 * `IncidentFeed`'s `useInfiniteQuery`, throwing away every page the reader had scrolled past, to
 * refetch a byte-identical answer. Leaving the field out of these two URLs makes the key say exactly
 * what the response depends on.
 */
function filterParams(f: Filters, bucketing: Bucketing): Param[] {
  return [
    ['category', f.category],
    ['granularity', bucketing === 'bucketed' ? f.granularity : undefined],
    ['from', f.from],
    ['to', f.to],
    ['prefecture', f.prefecture],
    // Sent only when it is not the server's own default, for the reason `domestic_only` is: the
    // two spellings mean the same request, and these URLs are React Query's cache keys. Adding a
    // redundant `source=digest` to every existing URL would evict every cached answer in the app
    // and change every bookmark, to ask exactly the question it already asked.
    ['source', f.source === DEFAULT_SOURCE ? undefined : f.source],
    // Only the `true` case is sent: `domestic_only=false` and no parameter mean the same thing to
    // the server, and the shorter URL is the one that gets shared.
    ['domestic_only', f.domesticOnly ? 'true' : undefined],
    // Last, so that adding these left every URL this client already built byte-identical — they are
    // React Query's cache keys, and a reordered query string is a cold cache for every reader.
    // `/api/aggregates` refuses both by name, so the tally panel never sets them.
    ['road_user', f.roadUser],
    ['county', f.county],
    // Appended rather than slotted beside `county` for the same cache-key reason.
    ['municipality', municipalityParam(f.municipalities)],
  ];
}

/** The paging and search params `/api/incidents` adds to the shared filter block. */
export interface IncidentQuery {
  limit?: number;
  cursor?: string | null;
  order?: string;
  q?: string | null;
}

/** The two extra dimensions of `/api/aggregates`; omitted means the server's own default. */
export interface AggregateQuery {
  metric?: string | null;
  scope?: string | null;
}

export function seriesUrl(f: Filters): string {
  return `/api/series${query(filterParams(f, 'bucketed'))}`;
}

export function statsUrl(f: Filters): string {
  return `/api/stats${query(filterParams(f, 'unbucketed'))}`;
}

/** The breakdown endpoint's URL. The category travels as the empty value {@link query} drops, which
 * is the only spelling the server accepts — it 400s on a named category rather than narrowing. */
export function statsByCategoryUrl(f: Omit<Filters, 'category'>): string {
  return `/api/stats/by-category${query(filterParams({ ...f, category: '' }, 'unbucketed'))}`;
}

export function aggregatesUrl(f: Filters & AggregateQuery): string {
  return `/api/aggregates${query([
    ...filterParams(f, 'bucketed'),
    ['metric', f.metric],
    ['scope', f.scope],
  ])}`;
}

export function incidentsUrl(f: Filters & IncidentQuery): string {
  return `/api/incidents${query([
    ...filterParams(f, 'unbucketed'),
    ['limit', f.limit],
    ['cursor', f.cursor],
    ['order', f.order],
    ['q', f.q],
  ])}`;
}

export const META_URL = '/api/meta';

/** The window and the two riders the Liiklusinfo endpoints take — and **no `source`**: the series
 * is in the path, and `routes.ts` 400s on the parameter rather than ignoring it. */
export interface TrafficWindow {
  from: string;
  to: string;
  /** A canonical county, the `UNSPECIFIED` sentinel, or null/undefined for no filter. */
  county?: string | null;
  roadUser?: RoadUser | null;
  /** EHAK labels and/or the `UNSPECIFIED` sentinel; empty or absent is no filter. */
  municipalities?: readonly string[] | null;
}

function trafficParams(f: TrafficWindow): Param[] {
  return [
    ['from', f.from],
    ['to', f.to],
    ['county', f.county],
    ['road_user', f.roadUser],
    ['municipality', municipalityParam(f.municipalities)],
  ];
}

export function trafficStatsUrl(f: TrafficWindow): string {
  return `/api/traffic/stats${query(trafficParams(f))}`;
}

export function trafficFacetsUrl(f: TrafficWindow): string {
  return `/api/traffic/facets${query(trafficParams(f))}`;
}

/**
 * The calibration takes no parameters at all — it is a claim about the whole corpus (P9), and a
 * stray parameter is a 400 rather than a second cache entry holding the same expensive body.
 */
export const TRAFFIC_CALIBRATION_URL = '/api/traffic/calibration';

// ─── Fetching ───────────────────────────────────────────────────────────────

/**
 * A response the API refused to give.
 *
 * A typed error rather than a formatted string, because two different callers need two different
 * things out of it and neither can parse them back out of prose:
 *
 *  - the **retry policy** (`App.tsx`) needs the status: a 400 is a request that will be refused
 *    identically forever, and retrying it only delays the message the reader needs to see;
 *  - the **UI** needs the field messages separately from a sentence it can print, because those
 *    messages are English machine text (`routes.ts` says "must be a YYYY-MM-DD date") and this
 *    interface is Estonian with no i18n layer. `describeApiError` decides what to say; the raw
 *    detail is shown as the machine value it is.
 *
 * `message` stays developer-facing and names the URL: a failure that reports only its status is
 * indistinguishable between the five endpoints a single page fires in parallel.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  /** `{param: message}` out of the documented 400 body, or `null` for any other shape. */
  readonly fields: Readonly<Record<string, string>> | null;

  constructor(url: string, status: number, fields: Readonly<Record<string, string>> | null) {
    const detail =
      fields === null
        ? ''
        : `: ${Object.entries(fields)
            .map(([field, message]) => `${field}: ${message}`)
            .join('; ')}`;
    super(`${url} failed (${status})${detail}`);
    this.name = 'ApiError';
    this.status = status;
    this.url = url;
    this.fields = fields;
  }
}

/**
 * The field messages out of a 400, or `null` for any body that is not the documented shape.
 *
 * The server answers `{error: {<param>: <message>}}` so the UI can put a complaint next to the
 * control that caused it (spec §6). Anything unparseable (an HTML error page from a proxy, an empty
 * body) is not an error here: the status alone still describes it.
 */
async function fieldMessages(res: Response): Promise<Record<string, string> | null> {
  try {
    const body: unknown = await res.json();
    if (typeof body !== 'object' || body === null || !('error' in body)) return null;
    const error = (body as { error: unknown }).error;
    if (typeof error !== 'object' || error === null) return null;
    const entries = Object.entries(error as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    );
    return entries.length === 0 ? null : Object.fromEntries(entries);
  } catch {
    return null;
  }
}

/** One GET, one parsed body, and an {@link ApiError} on anything else. */
async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new ApiError(url, res.status, await fieldMessages(res));
  return (await res.json()) as T;
}

/** What went wrong, in Estonian, and the machine text beside it rather than pasted into it. The
 * statuses group by what the reader can do: fix the filters, wait, or check the connection. */
export interface ErrorDescription {
  /** One Estonian sentence, safe to show alone. */
  text: string;
  /** English machine text from the server, or `null`. Never rendered as prose. */
  detail: string | null;
}

export function describeApiError(error: unknown): ErrorDescription {
  if (!(error instanceof ApiError)) {
    return {
      // Not "Ühendus serveriga ebaõnnestus": every caller prints this under `ErrorNote`'s own
      // "… laadimine ebaõnnestus.", and two sentences ending in the same word read as a stutter
      // rather than as two facts. This one says what happened instead of repeating that it failed.
      text: 'Server ei vastanud. Kontrolli internetiühendust ja proovi uuesti.',
      detail: error instanceof Error && error.message !== '' ? error.message : null,
    };
  }

  const detail =
    error.fields === null
      ? null
      : Object.entries(error.fields)
          .map(([field, message]) => `${field}: ${message}`)
          .join('; ');

  if (error.status === 400) {
    /*
     * "Try a shorter range or a bigger bucket" is advice about the *window*, and only a complaint
     * about the window earns it. `/api/incidents` also 400s on a cursor minted under the other
     * ordering and on an `order` value it does not know — neither is fixed by narrowing the dates,
     * and telling the reader to narrow them sends them to fiddle with a filter that was never the
     * problem while the real one stays broken. The 400 body is keyed by parameter name
     * (`routes.ts`), so the field names are the server's own answer to "what was wrong".
     */
    const aboutWindow =
      error.fields !== null &&
      (Object.hasOwn(error.fields, 'from') || Object.hasOwn(error.fields, 'to'));
    const advice = aboutWindow ? ' Proovi lühemat vahemikku või suuremat ajaühikut.' : '';
    return {
      text: `Päring oli vigane — kontrolli valitud ajavahemikku ja filtreid.${advice}`,
      detail,
    };
  }
  if (error.status === 404) {
    return { text: 'Sellist andmestikku ei ole.', detail };
  }
  if (error.status < 500) {
    return { text: `Päringut ei õnnestunud täita (viga ${error.status}).`, detail };
  }
  return {
    text: 'Server ei suutnud päringule vastata. Proovi mõne hetke pärast uuesti.',
    detail,
  };
}

export function fetchSeries(f: Filters): Promise<SeriesPoint[]> {
  return getJson<SeriesPoint[]>(seriesUrl(f));
}

export function fetchStats(f: Filters): Promise<Stats> {
  return getJson<Stats>(statsUrl(f));
}

export function fetchAggregates(f: Filters & AggregateQuery): Promise<AggregatePoint[]> {
  return getJson<AggregatePoint[]>(aggregatesUrl(f));
}

export function fetchIncidents(
  f: Filters & IncidentQuery,
): Promise<{ items: IncidentItem[]; next_cursor: string | null }> {
  return getJson<{ items: IncidentItem[]; next_cursor: string | null }>(incidentsUrl(f));
}

export function fetchMeta(): Promise<Meta> {
  return getJson<Meta>(META_URL);
}

export function fetchTrafficStats(f: TrafficWindow): Promise<TrafficStats> {
  return getJson<TrafficStats>(trafficStatsUrl(f));
}

export function fetchTrafficFacets(f: TrafficWindow): Promise<TrafficFacets> {
  return getJson<TrafficFacets>(trafficFacetsUrl(f));
}

export function fetchTrafficCalibration(): Promise<TrafficCalibration> {
  return getJson<TrafficCalibration>(TRAFFIC_CALIBRATION_URL);
}
